import { useState, useRef, useEffect } from 'react';
import reverbEcho, { subscribeUserCampaign, leaveUserCampaign } from 'services/reverbEcho';

export const useCampaignProgress = () => {
    const [progress, setProgress] = useState(0);
    const [isSending, setIsSending] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [error, setError] = useState('');

    const activeCampaignIdRef = useRef(null);
    const activeCampaignUserIdRef = useRef(null);

    useEffect(() => {
        reverbEcho.getEcho();
    }, []);

    const stopCampaignChannel = () => {
        if (activeCampaignUserIdRef.current) {
            leaveUserCampaign(activeCampaignUserIdRef.current);
            activeCampaignUserIdRef.current = null;
        }
        activeCampaignIdRef.current = null;
    };

    const resetCampaign = () => {
        stopCampaignChannel();
        setIsSending(false);
        setProgress(0);
        setIsModalVisible(false);
    };

    const parsePercent = (event) => {
        const raw = event?.percent;
        if (typeof raw === 'number') return raw;
        if (typeof raw === 'string') {
            const n = Number(raw.replace('%', '').trim());
            return Number.isFinite(n) ? n : null;
        }
        if (typeof event?.sent === 'number' && typeof event?.total === 'number' && event.total > 0) {
            return Math.round((event.sent / event.total) * 100);
        }
        return null;
    };

    const startCampaignChannel = (userId, campaignId) => {
        stopCampaignChannel();
        if (!userId || !campaignId) return;

        activeCampaignIdRef.current = campaignId;
        activeCampaignUserIdRef.current = userId;

        const safeEvent = (evt) => {
            if (typeof evt !== 'string') return evt;
            try { return JSON.parse(evt); } catch { return { raw: evt }; }
        };

        const shouldHandle = (event) => {
            const eventCampaignId = event?.campaign_id;
            if (!eventCampaignId) return true;
            return String(eventCampaignId) === String(activeCampaignIdRef.current);
        };

        subscribeUserCampaign(userId, {
            onProgress: (evt) => {
                console.log("Progress at " + new Date().toLocaleTimeString(), evt);
                const event = safeEvent(evt);
                if (!shouldHandle(event)) return;
                const pct = parsePercent(event);
                if (pct === null) return;
                const bounded = Math.max(0, Math.min(100, pct));
                setProgress((prev) => Math.max(prev, bounded));
            },
            onCompleted: (evt) => {
                console.log("Completed at " + new Date().toLocaleTimeString(), evt);
                const event = safeEvent(evt);
                if (!shouldHandle(event)) return;
                setProgress(100);
                setIsSending(false);
                stopCampaignChannel();
            },
            onFailed: (evt) => {
                console.log("Failed at " + new Date().toLocaleTimeString(), evt);
                const event = safeEvent(evt);
                if (!shouldHandle(event)) return;
                setError(event?.error || 'Campaign processing failed');
                setIsSending(false);
                setIsModalVisible(false);
                stopCampaignChannel();
            },
        });
    };

    // Navigation and refresh protection
    useEffect(() => {
        const handleBeforeUnload = (e) => {
            if (isSending) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        const handlePopstate = (e) => {
            if (isSending) {
                // This is a bit tricky with just a hook, but we can at least log or trigger a callback
                console.warn('Navigation blocked due to active campaign');
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopstate);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopstate);
        };
    }, [isSending]);

    useEffect(() => {
        return () => {
            if (activeCampaignUserIdRef.current) {
                leaveUserCampaign(activeCampaignUserIdRef.current);
            }
        };
    }, []);

    return {
        progress,
        setProgress,
        isSending,
        setIsSending,
        isModalVisible,
        setIsModalVisible,
        error,
        setError,
        startCampaignChannel,
        stopCampaignChannel,
        resetCampaign
    };
};
